import {
  Home, Users, Briefcase, TrendingUp, BarChart3, ArrowUpRight, ClipboardList,
  Upload, Settings, Ticket, Scale, Database,
} from "lucide-react";

/* ===========================================================================
   Igualdad Talention — MVP (demo)
   Estética inspirada en la herramienta real. Datos ficticios, cálculos reales.
   =========================================================================== */

/* Paleta para gráficas (ECharts / SVG), que no pueden leer CSS custom
   properties. Duplica los tokens de :root a propósito; `src/igualdad-data.test.js`
   verifica que no se desvía, así que si cambias un color en styles.css y no
   aquí, la suite se pone roja.

   Los nombres dicen lo que son: hasta C3 esto tenía `teal` para el naranja de
   marca y dos parejas de alias del mismo color (teal/orange, navy/ink). */
export const C = {
  brand:      "#ff8901",  // --orange-500
  brandHover: "#e67a00",  // --orange-600
  brandLight: "#ffc46f",  // --brand-300
  charcoal:   "#363637",  // --charcoal-800
  textMuted:  "#6f6f70",  // --charcoal-500
  border:     "#d9d8d9",  // --gray-200
  pageBg:     "#f7f6f7",  // --gray-50
  surface:    "#ffffff",  // --white
  success:    "#1f8a5b",  // --success
  danger:     "#c93a2a",  // --danger
  // sexo
  H:          "#6f6f70",  // --charcoal-500
  M:          "#ff8901",  // --orange-500
};
export const LOGO_URL = "https://d25ltszcjeom5i.cloudfront.net/245134/mvaonwdijc/logo-talention-positivo.png";
/* Serie categórica: marca y charcoal alternando con sus versiones claras. */
export const SERIES = ["#ff8901", "#363637", "#ffb14d", "#a5a4a5", "#ffedd6", "#bfbebe"];

// ---- helpers ---------------------------------------------------------------
export const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.round(n || 0));
export const pct1 = (n) => `${n < 0 ? "−" : ""}${Math.abs(n).toFixed(1).replace(".", ",")} %`;
export const pct0 = (n) => `${Math.round(n)} %`;

// ---- matrices de incrementos ----------------------------------------------
export const BANDA_BUCKETS = ["<80", "80–90", "90–100", "100–110", "110–120", ">120"];
export const RESULT_BUCKETS = ["A", "B", "C", "D"];
export const MATRICES = {
  realista: { A: [6, 5, 4, 3, 2, 0], B: [5, 4, 3, 2, 1, 0], C: [4, 3, 2, 1, 0, 0], D: [2, 1, 0, 0, 0, 0] },
  optimista: { A: [8, 7, 6, 4.5, 3, 1], B: [7, 6, 4.5, 3, 2, 0.5], C: [5.5, 4.5, 3, 2, 1, 0], D: [3.5, 2.5, 1.5, 0.5, 0, 0] },
  pesimista: { A: [3, 2.5, 2, 1.5, 1, 0], B: [2.5, 2, 1.5, 1, 0.5, 0], C: [2, 1.5, 1, 0.5, 0, 0], D: [1, 0.5, 0, 0, 0, 0] },
};

// ---- chrome / navegación ---------------------------------------------------
export const MENU = [
  { key: "inicio", label: "Inicio", icon: Home },
  { key: "personas", label: "Personas", icon: Users },
  { key: "puestos", label: "Puestos", icon: Briefcase },
  { key: "registro", label: "Registro retributivo", icon: Scale, isNew: true },
  { key: "estructura", label: "Políticas retributivas", icon: TrendingUp },
  { key: "analytics", label: "People Analytics", icon: BarChart3 },
  { key: "incrementos", label: "Incrementos salariales", icon: ArrowUpRight },
  { key: "evaluaciones", label: "Evaluaciones", icon: ClipboardList },
  { key: "carga", label: "Carga masiva", icon: Upload },
  { key: "catalogos", label: "Catálogos", icon: Database },
  { key: "config", label: "Configuración", icon: Settings },
  { key: "tickets", label: "Tickets", icon: Ticket },
];

export const CRUMBS = {
  inicio: ["Inicio"], personas: ["Inicio", "Personas"], puestos: ["Inicio", "Puestos"],
  registro: ["Inicio", "Registro retributivo"], estructura: ["Inicio", "Políticas retributivas", "Estructura salarial"],
  analytics: ["Inicio", "People Analytics"], incrementos: ["Inicio", "Incrementos salariales"],
  evaluaciones: ["Inicio", "Evaluaciones"], carga: ["Inicio", "Carga masiva"],
  catalogos: ["Inicio", "Catálogos"],
  config: ["Inicio", "Configuración"], tickets: ["Inicio", "Tickets"],
};

// ---- registro retributivo (riesgo) -----------------------------------------
/* Escala de riesgo. `alto` es el naranja quemado intermedio entre la marca
   (--brand) y el rojo de peligro (--danger); el DS no define ese paso, así que
   se deriva aquí. RTONE_BG son los fondos suaves equivalentes: sustituyen al
   antipatrón de concatenar alfa al hex (`tone + "1F"`). */
export const RTONE = { bajo: C.success, medio: C.brand, alto: "#d9682e", critico: C.danger };
export const RTONE_BG = {
  bajo: "#e6f4ed",     // --success-bg
  medio: "#fff6ea",    // --orange-50
  alto: "#fdeee6",     // derivado de RTONE.alto
  critico: "#fbe9e6",  // --danger-bg
};

/* Constantes de integración (D-B05): no existen en B/src/data.js.
   Traducen entre las 12 `view` key del origen y las 12 rutas del hash router de App A. */
export const RUTA_POR_CLAVE = {
  inicio: "/igualdad",
  personas: "/igualdad/personas",
  puestos: "/igualdad/puestos",
  registro: "/igualdad/registro",
  estructura: "/igualdad/estructura",
  analytics: "/igualdad/analytics",
  incrementos: "/igualdad/incrementos",
  evaluaciones: "/igualdad/evaluaciones",
  carga: "/igualdad/carga",
  catalogos: "/igualdad/catalogos",
  config: "/igualdad/config",
  tickets: "/igualdad/tickets",
};
export const CLAVE_POR_RUTA = Object.fromEntries(
  Object.entries(RUTA_POR_CLAVE).map(([k, v]) => [v, k])
);
